import React from 'react';
import { Combobox } from 'components/ui/combobox';
import { MultiSelectCombobox } from 'components/ui/multi-select-combobox';

export interface Level {
  id: number | string;
  name: string;
  visible?: boolean;
  selected?: string[] | null;
  options?: Array<{ ou_code: string; ou_name: string }>;
  locked?: string[] | null;
}

interface DropdownsProps {
  year: string;
  years?: string[];
  levels: Level[];
  loading: boolean;
  onSelectChange: (levelId: number | string, value: string[]) => void;
  onBatchSelect?: (updates: Array<{ levelId: number | string; value: string[] }>) => void;
  onYearChange?: (year: string) => void;
}

const LOCKED_MULTI_VALUE = '__locked_multiple__';

const Dropdowns: React.FC<DropdownsProps> = ({
  year,
  years,
  levels,
  loading,
  onSelectChange,
  onBatchSelect,  // unused now but keep for API compatibility
  onYearChange,
}) => {
  const getVisibleDesc = React.useCallback(
    () =>
      (levels || [])
        .filter((l) => Boolean(l?.visible))
        .sort((a, b) => Number(b.id) - Number(a.id)), // 5 -> 1
    [levels]
  );

  const visibleLevelsDesc = getVisibleDesc();

  return (
    <div
      className="grid gap-4 items-start w-full"
      style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))' }}
    >
      {/* Year */}
      <div className="min-w-0">
        <div className="space-y-1">
          <label className="text-sm font-medium text-gray-700">Year</label>
          <Combobox
            options={(years && years.length > 0 ? years : [year]).map((y) => ({
              value: y,
              label: y,
            }))}
            value={year}
            disabled={loading}
            onValueChange={(val) => {
              if (!val) return;
              if (onYearChange) onYearChange(val);
            }}
            placeholder="Select year..."
            buttonClassName="h-10 px-2.5 text-sm"
          />
        </div>
      </div>

      {/* Levels */}
      {visibleLevelsDesc.map((level) => {
        const baseOpts = Array.isArray(level.options)
          ? level.options.map((opt) => ({
              value: opt.ou_code,
              label: opt.ou_name,
            }))
          : [];

        const lockedCodes = Array.isArray(level.locked) ? level.locked : [];
        const isLockedLevel = lockedCodes.length > 0;
        const hasMultipleLocked = lockedCodes.length > 1;

        const opts =
          Number(level.id) !== 1 && isLockedLevel && hasMultipleLocked
            ? [
                ...baseOpts,
                {
                  value: LOCKED_MULTI_VALUE,
                  label: `${lockedCodes.length} selected`,
                },
              ]
            : baseOpts;

        return (
          <div className="min-w-0" key={String(level.id)}>
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">
                {level.name}
              </label>

              {Number(level.id) === 1 ? (
                // Level 1 = multi-select
                <MultiSelectCombobox
                  options={baseOpts}
                  value={
                    isLockedLevel
                      ? lockedCodes
                      : Array.isArray(level.selected)
                      ? level.selected
                      : []
                  }
                  onValueChange={(val) => {
                    if (isLockedLevel) return; 
                    onSelectChange(level.id, val);
                  }}
                  placeholder="-- select --"
                  searchPlaceholder={`Search ${level.name.toLowerCase()}...`}
                  emptyText={`No ${level.name.toLowerCase()} found.`}
                  disabled={loading || isLockedLevel}
                  buttonClassName="h-10 px-2.5 text-sm"
                />
              ) : (
                // Other levels = single-select
                <Combobox
                  options={opts}
                  value={
                    isLockedLevel
                      ? hasMultipleLocked
                        ? LOCKED_MULTI_VALUE
                        : lockedCodes[0] ?? ''
                      : Array.isArray(level.selected) && level.selected.length > 0
                      ? level.selected[0]
                      : ''
                  }
                  onValueChange={(val) => {
                    if (isLockedLevel) return; 
                    onSelectChange(level.id, val ? [val] : []);
                  }}
                  placeholder="-- select --"
                  searchPlaceholder={`Search ${level.name.toLowerCase()}...`}
                  emptyText={`No ${level.name.toLowerCase()} found.`}
                  disabled={loading || isLockedLevel}
                  buttonClassName="h-10 px-2.5 text-sm"
                />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default Dropdowns;
